// @ts-check
// Prévia da mensagem do bot no estilo da conversa do WhatsApp (contrato de bots, seção 22.2, `BotMessagePreview`): o
// balão com o cabeçalho, o texto (variáveis com valores de exemplo), o rodapé, os botões embaixo do balão e, na lista, o
// botão que abre as linhas (mostradas abertas na prévia). Também é o balão do "Testar bot".
import { html } from 'htm/preact';
import { Icon } from '../../ui/icon.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../lib/bot-message-limits.js').BotMessagePreviewModel} BotMessagePreviewModel
 */

/**
 * @param {{ model: BotMessagePreviewModel, showRows?: boolean, onOption?: ((option: { id: string, title: string }) => void) | null,
 *   optionIds?: string[] | null, compact?: boolean }} props
 *   onOption e optionIds: no "Testar bot", tocar num botão ou numa linha responde ao bot
 */
export function BotMessagePreview({ model, showRows = true, onOption = null, optionIds = null, compact = false }) {
  const titles = model.kind === 'BUTTONS' ? model.buttons.map((title) => ({ title, description: null })) : model.rows;
  /** @param {number} position */
  const optionOf = (position) => ({ id: optionIds?.[position] ?? String(position), title: titles[position]?.title ?? '' });
  return html`<div class="bot-bubble-wrap" data-compact=${compact ? 'true' : undefined}>
    <div class="bot-bubble" data-direction="outgoing">
      ${model.kind === 'MEDIA'
        ? html`<div class="bot-bubble-media"><${Icon} name=${model.mediaKind === 'IMAGE' ? 'IconPhoto' : model.mediaKind === 'AUDIO' ? 'IconMicrophone' : 'IconFileText'} size=${20} />
            <span>${t(`bots.preview.media.${model.mediaKind ?? 'DOCUMENT'}`)}</span></div>`
        : null}
      ${model.header ? html`<p class="bot-bubble-header">${model.header}</p>` : null}
      ${model.body ? html`<p class="bot-bubble-text">${model.body}</p>` : html`<p class="bot-bubble-text" data-empty="true">${t('bots.preview.emptyText')}</p>`}
      ${model.footer ? html`<p class="bot-bubble-footer">${model.footer}</p>` : null}
      ${model.kind === 'LIST'
        ? html`<button type="button" class="bot-bubble-list-button" disabled=${!onOption} tabindex=${onOption ? 0 : -1}>
            <${Icon} name="IconList" size=${14} /><span>${model.listButton || t('bots.preview.listButtonFallback')}</span>
          </button>`
        : null}
    </div>
    ${model.kind === 'BUTTONS' && model.buttons.length > 0
      ? html`<div class="bot-bubble-buttons" role=${onOption ? 'group' : undefined} aria-label=${onOption ? t('bots.test.optionsLabel') : undefined}>
          ${model.buttons.map((title, position) => (onOption
            ? html`<button type="button" key=${position} class="bot-bubble-button" onClick=${() => onOption(optionOf(position))}>${title}</button>`
            : html`<span key=${position} class="bot-bubble-button">${title}</span>`))}
        </div>`
      : null}
    ${model.kind === 'LIST' && showRows && model.rows.length > 0
      ? html`<ul class="bot-bubble-rows" aria-label=${t('bots.preview.rowsLabel')}>
          ${model.rows.map((row, position) => html`<li key=${position}>
            ${onOption
              ? html`<button type="button" class="bot-bubble-row" onClick=${() => onOption(optionOf(position))}>
                  <span class="bot-bubble-row-title">${row.title}</span>${row.description ? html`<span class="bot-bubble-row-description">${row.description}</span>` : null}
                </button>`
              : html`<span class="bot-bubble-row"><span class="bot-bubble-row-title">${row.title}</span>${row.description ? html`<span class="bot-bubble-row-description">${row.description}</span>` : null}</span>`}
          </li>`)}
        </ul>`
      : null}
    ${model.isDynamic ? html`<p class="bot-bubble-note">${t('bots.preview.dynamicNote')}</p>` : null}
  </div>`;
}
